// Deploy freshness: every build embeds a BUILD_ID and ships a /version.json
// with the same id. Running tabs poll it; a mismatch means a newer build was
// deployed. The app then reloads itself the moment that is non-disruptive
// (tab hidden, not in a call) or offers a Reload pill while the tab is active.
// This closes the version-skew window where old bundles talk to a new API.

declare const __BUILD_ID__: string;

const POLL_MS = 4 * 60 * 1000;

/** Starts polling for a newer deployed build; returns a cleanup function.
 *  onUpdate fires once, the first time a different build id is seen. */
export function watchForUpdates(onUpdate: () => void): () => void {
  let stopped = false;
  let fired = false;
  const check = async () => {
    if (stopped || fired) return;
    try {
      const r = await fetch(`/version.json?t=${Date.now()}`, { cache: 'no-store' });
      if (!r.ok) return;
      const v = await r.json();
      if (v?.build && v.build !== __BUILD_ID__ && !fired) {
        fired = true;
        onUpdate();
      }
    } catch { /* offline / deploy in progress — try again next tick */ }
  };
  const timer = window.setInterval(check, POLL_MS);
  // Returning to a long-backgrounded tab is the most common stale moment.
  const onVis = () => { if (document.visibilityState === 'visible') check(); };
  document.addEventListener('visibilitychange', onVis);
  window.setTimeout(check, 20_000); // first check shortly after load
  return () => {
    stopped = true;
    window.clearInterval(timer);
    document.removeEventListener('visibilitychange', onVis);
  };
}
